import type { ClientDto, CreateClientDto, UpdateClientDto } from '@schwrz/shared';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { api } from '../shared/api/client';

export const useClientsStore = defineStore('clients', () => {
  const items = ref<ClientDto[]>([]);
  const archivedItems = ref<ClientDto[]>([]);
  const loading = ref(false);
  const loaded = ref(false);
  const archivedLoading = ref(false);
  const archivedLoaded = ref(false);
  const error = ref<string | null>(null);
  const selectedId = ref<string | null>(null);
  const openSidebarClientId = ref<string | null>(null);
  /** Legacy openClientActionMenuId — only one company "…" menu open at a time. */
  const openActionMenuClientId = ref<string | null>(null);
  /** Legacy formProject host: inline "+ Новый проект" form inside a company dropdown. */
  const projectFormClientId = ref<string | null>(null);

  const sorted = computed<ClientDto[]>(() =>
    [...items.value].sort((a, b) => {
      if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
      return a.sortOrder - b.sortOrder;
    }),
  );

  const selected = computed<ClientDto | null>(
    () => items.value.find((c) => c.id === selectedId.value) ?? null,
  );

  /** Lookup by route param — numeric public id or legacy UUID (active + archived caches). */
  function getByRef(ref: string): ClientDto | undefined {
    const inActive = (list: ClientDto[]): ClientDto | undefined => {
      if (/^\d+$/.test(ref)) {
        const n = Number(ref);
        return list.find((c) => c.number === n);
      }
      return list.find((c) => c.id === ref);
    };
    return inActive(items.value) ?? inActive(archivedItems.value);
  }

  function upsertActive(client: ClientDto): void {
    items.value = [...items.value.filter((c) => c.id !== client.id), client];
  }

  function upsertArchived(client: ClientDto): void {
    archivedItems.value = [...archivedItems.value.filter((c) => c.id !== client.id), client];
  }

  async function load(force = false): Promise<void> {
    if (loaded.value && !force) return;
    loading.value = true;
    error.value = null;
    try {
      const res = await api<{ clients: ClientDto[] }>('/api/clients');
      items.value = res.clients;
      loaded.value = true;
    } catch (err) {
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function loadArchived(force = false): Promise<void> {
    if (archivedLoaded.value && !force) return;
    if (archivedLoading.value) return;
    archivedLoading.value = true;
    error.value = null;
    try {
      const res = await api<{ clients: ClientDto[] }>('/api/clients?includeArchived=true');
      archivedItems.value = res.clients.filter((c) => c.archived);
      archivedLoaded.value = true;
    } catch (err) {
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      archivedLoading.value = false;
    }
  }

  /** Resolve a client from active list, falling back to the archived cache. */
  async function resolveByRef(ref: string): Promise<ClientDto | undefined> {
    await load();
    const client = getByRef(ref);
    if (client) return client;
    await loadArchived();
    return getByRef(ref);
  }

  async function create(payload: CreateClientDto): Promise<ClientDto> {
    const created = await api<ClientDto>('/api/clients', { method: 'POST', body: payload });
    upsertActive(created);
    return created;
  }

  async function update(id: string, payload: UpdateClientDto): Promise<ClientDto> {
    const updated = await api<ClientDto>(`/api/clients/${id}`, {
      method: 'PATCH',
      body: payload,
    });

    if (payload.archived === true) {
      items.value = items.value.filter((c) => c.id !== id);
      upsertArchived(updated);
    } else if (payload.archived === false) {
      archivedItems.value = archivedItems.value.filter((c) => c.id !== id);
      upsertActive(updated);
    } else {
      if (updated.archived) {
        items.value = items.value.filter((c) => c.id !== id);
        upsertArchived(updated);
      } else {
        archivedItems.value = archivedItems.value.filter((c) => c.id !== id);
        upsertActive(updated);
      }
    }

    return updated;
  }

  async function remove(id: string): Promise<void> {
    await api<void>(`/api/clients/${id}`, { method: 'DELETE' });
    items.value = items.value.filter((c) => c.id !== id);
    archivedItems.value = archivedItems.value.filter((c) => c.id !== id);
    if (selectedId.value === id) selectedId.value = null;
    if (openSidebarClientId.value === id) openSidebarClientId.value = null;
  }

  /**
   * Drag-reorder: move `draggedId` to just before `targetId`, then renumber
   * sortOrder sequentially and PATCH the changed clients (legacy reorderClients).
   */
  async function reorder(
    draggedId: string,
    targetId: string,
    insertAfter = false,
  ): Promise<void> {
    if (draggedId === targetId) return;
    const order = items.value.map((c) => c.id);
    const from = order.indexOf(draggedId);
    const targetIndex = order.indexOf(targetId);
    if (from < 0 || targetIndex < 0) return;

    const reordered = [...order];
    reordered.splice(from, 1);
    let insertIndex = targetIndex;
    if (from < targetIndex) insertIndex -= 1;
    if (insertAfter) insertIndex += 1;
    insertIndex = Math.max(0, Math.min(insertIndex, reordered.length));
    reordered.splice(insertIndex, 0, draggedId);

    await Promise.all(
      reordered
        .map((id, idx) => ({ id, idx }))
        .filter(({ id, idx }) => {
          const c = items.value.find((x) => x.id === id);
          return c !== undefined && c.sortOrder !== idx;
        })
        .map(({ id, idx }) => update(id, { sortOrder: idx })),
    );
  }

  function select(id: string | null): void {
    selectedId.value = id;
  }

  function setSidebarOpen(id: string | null): void {
    openSidebarClientId.value = id;
  }

  function setActionMenuOpen(id: string | null): void {
    openActionMenuClientId.value = id;
  }

  function showProjectForm(clientId: string | null): void {
    projectFormClientId.value = clientId;
  }

  return {
    items,
    archivedItems,
    sorted,
    selected,
    selectedId,
    openSidebarClientId,
    openActionMenuClientId,
    projectFormClientId,
    loading,
    loaded,
    archivedLoading,
    archivedLoaded,
    error,
    load,
    loadArchived,
    resolveByRef,
    getByRef,
    create,
    update,
    remove,
    select,
    setSidebarOpen,
    setActionMenuOpen,
    showProjectForm,
    reorder,
  };
});
